import { useEffect, useRef, useState } from 'react';
import type { HostingPleskAutomationView } from '@tosho/contracts';
import { hostingPleskApi } from './hosting-plesk-api';
import { PrototypeIcon } from './PrototypeIcon';

/** La connexion auditée est réconciliée côté serveur. Cette vue ne fait que
 * relire le suivi local ; suspension/reprise restent des commandes explicites. */
export function HostingPleskAutomationControls({
  serverId,
  csrfToken,
  disabled,
  onBusyChange,
  onFailure,
}: {
  readonly serverId: string;
  readonly csrfToken: string;
  readonly disabled: boolean;
  readonly onBusyChange: (busy: boolean) => void;
  readonly onFailure: (reason: unknown) => void;
}): React.JSX.Element {
  const [view, setView] = useState<HostingPleskAutomationView | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const failure = useRef(onFailure);
  failure.current = onFailure;
  useEffect(() => {
    alive.current = true;
    const read = (): void => {
      void hostingPleskApi
        .readAutomation(serverId)
        .then((value) => {
          if (alive.current) setView(value);
        })
        .catch((reason) => {
          if (alive.current) failure.current(reason);
        });
    };
    read();
    const timer = window.setInterval(read, 30_000);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [serverId]);
  async function toggle(): Promise<void> {
    if (!view || busy || disabled) return;
    setBusy(true);
    onBusyChange(true);
    try {
      const result = await hostingPleskApi.setAutomation(
        serverId,
        view.revision,
        !view.enabled,
        csrfToken,
      );
      if (alive.current) setView(result);
    } catch (reason) {
      if (alive.current) failure.current(reason);
    } finally {
      if (alive.current) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  }
  return (
    <section
      className="hosting-plesk-automation"
      aria-label="Actualisation automatique Plesk"
      aria-busy={busy}
    >
      <header>
        <PrototypeIcon name="refresh-cw" />
        <h3>Actualisation automatique</h3>
      </header>
      <div>
        <p>
          Inventaire des domaines et listes de sauvegardes chaque heure. Informations techniques une
          fois par jour, par petits lots. Lecture seule ; vos rattachements et déclarations sont
          conservés.
        </p>
        <p role="status">
          {view
            ? view.pendingAutomatic
              ? 'Collecte automatique en préparation avec la connexion enregistrée.'
              : view.enabled
                ? 'Actualisation activée.'
                : !view.authorizationIsCurrent
                  ? 'Autorisation à renouveler avec l’accès actuel.'
                  : view.revision === null
                    ? 'En attente d’une connexion Plesk autorisée.'
                    : 'Actualisation suspendue.'
            : 'Chargement de la configuration…'}
        </p>
        {view?.lastSuccessAt ? (
          <p>Dernière lecture réussie : {new Date(view.lastSuccessAt).toLocaleString('fr-FR')}.</p>
        ) : null}
        {view?.lastErrorCode ? (
          <p className="hosting-plesk-automation__error tosho-feedback is-error" role="alert">
            Certaines lectures de la dernière collecte ont échoué. Les derniers résultats réussis
            restent disponibles dans les fiches concernées.
          </p>
        ) : null}
        {view?.enabled && view.nextInventoryAt ? (
          <p>
            Prochain inventaire prévu : {new Date(view.nextInventoryAt).toLocaleString('fr-FR')}.
          </p>
        ) : null}
        <button
          type="button"
          className="hosting-servers__primary"
          disabled={disabled || busy || !view || view.pendingAutomatic || view.revision === null}
          onClick={() => {
            void toggle();
          }}
        >
          <PrototypeIcon name="refresh-cw" />
          {busy
            ? 'Enregistrement…'
            : view?.pendingAutomatic
              ? 'Démarrage automatique…'
              : view?.enabled
                ? 'Suspendre l’actualisation'
                : view?.revision !== null
                  ? 'Reprendre l’actualisation'
                  : 'Connexion Plesk à vérifier'}
        </button>
      </div>
    </section>
  );
}
